import type { EnrichedReadyToSharePlatformPostV2 } from 'src/app/shared/api/readyToShare';

import type { SharingModalFactoryPropsWithResource } from '../../factories/types';

import type { ActionsReturnType, GroupSharingHookProps, PostSharingHookProps, StorySharingHookProps } from './types';
import { useFacebookGroupPostSharing } from './useFacebookGroupPostSharing';
import { useFacebookPostSharing } from './useFacebookPostSharing';
import { useFacebookStorySharing } from './useFacebookStorySharing';
import { useInstagramPostSharing } from './useInstagramPostSharing';
import { useInstagramStorySharing } from './useInstagramStorySharing';
import { useRedditGroupPostSharing } from './useRedditGroupPostSharing';

type SharingHook<P> = (props: P) => ActionsReturnType;
type Executor = (props: SharingModalFactoryPropsWithResource) => ActionsReturnType;

const createEntry = <P>(
	hook: SharingHook<P>,
	getProps: (props: SharingModalFactoryPropsWithResource) => P,
): Executor => {
	return (props) => {
		const prepared = getProps(props);
		return hook(prepared);
	};
};

// Add more executors as more post types are supported
export const EXECUTORS: Readonly<Record<EnrichedReadyToSharePlatformPostV2['__typename'], Executor>> = {
	ReadyToShareFacebookPost: createEntry(useFacebookPostSharing, (props): PostSharingHookProps => {
		const { trackingProps, sharingResource } = props;
		return {
			message: sharingResource.message,
			previewImageUrl: '', // this post type doesn't use previewImageUrl for actions but is required by the type
			trackingProps,
			shareUrl: sharingResource.shareUrl,
		};
	}),
	ReadyToShareFacebookGroupsPost: createEntry(useFacebookGroupPostSharing, (props): GroupSharingHookProps => {
		const { trackingProps, sharingResource, recommendedGroup } = props;
		return {
			recommendedGroup,
			message: sharingResource.message,
			trackingProps,
			shareUrl: sharingResource.shareUrl,
		};
	}),
	ReadyToShareFacebookStory: createEntry(useFacebookStorySharing, (props): StorySharingHookProps => {
		const { trackingProps, sharingResource } = props;
		return { shareUrl: sharingResource.shareUrl, previewImageUrl: sharingResource.previewImageUrl, trackingProps };
	}),
	ReadyToShareInstagramPost: createEntry(useInstagramPostSharing, (props): PostSharingHookProps => {
		const { trackingProps, sharingResource } = props;
		return {
			message: sharingResource.message,
			previewImageUrl: sharingResource.previewImageUrl,
			trackingProps,
			shareUrl: sharingResource.shareUrl,
		};
	}),
	ReadyToShareInstagramStory: createEntry(useInstagramStorySharing, (props): StorySharingHookProps => {
		const { trackingProps, sharingResource } = props;
		return { shareUrl: sharingResource.shareUrl, previewImageUrl: sharingResource.previewImageUrl, trackingProps };
	}),
	ReadyToShareRedditGroupsPost: createEntry(useRedditGroupPostSharing, (props): GroupSharingHookProps => {
		const { trackingProps, petition, sharingResource, recommendedGroup } = props;
		return {
			recommendedGroup,
			message: sharingResource.message,
			trackingProps,
			petitionTitle: petition.ask,
			shareUrl: sharingResource.shareUrl,
		};
	}),
};
